import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ========== Animations ========== */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`0%{transform:rotate(0)}100%{transform:rotate(360deg)}`;

/* ========== Layout ========== */
const Page = styled.div`
  max-width: 1240px;
  margin: clamp(16px, 3vw, 32px) auto;
  padding: clamp(12px, 2.5vw, 24px);
`;

const Header = styled.div`
  display: grid;
  gap: 8px;
  margin-bottom: 14px;
  text-align: center;
`;
const Title = styled.h1`
  font-size: clamp(1.2rem, 2vw, 1.6rem);
  font-weight: 700;
`;

/* ========== Order Card ========== */
const OrderCard = styled.section`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0,0,0,.06);
  padding: clamp(12px, 2vw, 18px);
  margin-bottom: 16px;
  animation: ${fadeIn} .35s ease both;
`;

const OrderTop = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px;
  align-items: center; justify-content: space-between;
  padding-bottom: 10px; border-bottom: 1px dashed #ececec;
`;

const Row = styled.div`
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
`;

const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: .8rem; padding: 6px 10px; border-radius: 999px;
  background: ${p => p.variant === "dark" ? "#111827" : "rgba(17,24,39,.06)"};
  color: ${p => p.variant === "dark" ? "#fff" : "#111827"};
  border: 1px solid rgba(17,24,39,.08);
`;

const Meta = styled.span`
  color:#333; font-weight:600;
  small{ color:#666; font-weight:400; margin-left:8px;}
`;

/* ========== Items Grid ========== */
const ItemsGrid = styled.ul`
  list-style:none; padding:0; margin: 14px 0 0;
  display:grid; gap:12px;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
`;

const Item = styled.li`
  border:1px solid #f1f1f1; border-radius:12px; padding:12px;
  display:grid; grid-template-columns: 92px 1fr; gap:12px; background:#fff;
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover{ transform: translateY(-2px); box-shadow:0 10px 24px rgba(0,0,0,.06); }
`;

const Thumb = styled.img`
  width:92px; height:92px; object-fit:contain;
  border-radius:10px; background:#fff; border:1px solid #f3f3f3;
`;

const Name = styled.h3`
  font-size:.98rem; margin:0 0 6px; font-weight:650; color:#111827;
`;

const ChipRow = styled.div`
  display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;
`;

/* ========== Price ========== */
const Price = styled.div`
  margin-top:8px; font-size:.95rem; font-weight:700;
  .old{ text-decoration: line-through; color:#888; font-weight:500; margin-right:8px; }
`;

/* ========== Set Contents ========== */
const SetToggle = styled.button`
  margin-top:8px; border:1px dashed #e5e7eb; background:#f9fafb; color:#111827;
  padding:8px 10px; font-size:.9rem; border-radius:10px; cursor:pointer;
  &:hover{ background:#f3f4f6; }
`;

const SetList = styled.ul`
  list-style:none; padding:10px 0 0 0; margin:0; display:grid; gap:8px;
`;

const SetItem = styled.li`
  display:flex; gap:10px; align-items:center;
  img{ width:48px; height:48px; object-fit:contain; border:1px solid #f1f1f1; border-radius:8px; background:#fff; }
  .txt{ line-height:1.2; }
  .nm{ font-size:.9rem; font-weight:600; }
  .sm{ font-size:.82rem; color:#555; }
`;

/* ========== Footer ========== */
const Total = styled.div`
  margin-top: 12px; text-align:right; font-weight:800; font-size:1rem;
`;

/* ========== Empty / Loading ========== */
const Empty = styled.div`
  background:#f8f8f8; border:1px dashed #ddd; color:#333;
  border-radius:12px; padding:14px; text-align:center;
`;
const LoaderWrap = styled.div`
  min-height:40vh; display:flex; align-items:center; justify-content:center;
`;
const Spinner = styled.div`
  border:4px solid rgba(0,0,0,.08);
  border-top:4px solid #000; border-radius:50%; width:42px; height:42px;
  animation:${spin} 1s linear infinite;
`;

/* ========== Utils ========== */
const asArray = v => (Array.isArray(v) ? v : []);
const formatMoney = (v) => `${parseFloat(v ?? 0).toFixed(2)} GEL`;
const isPosNumber = (v) => {
  if (v === null || v === undefined) return false;
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
};
const trimZeros = (v) => {
  const s = String(v ?? "");
  return s.includes(".") ? s.replace(/\.0+$/,"").replace(/(\.\d*[1-9])0+$/,"$1") : s;
};
const numOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const typeLabel = (type, lang) => {
  if (!type) return "";
  if (type === "gift_set") return lang === "ka" ? "სასაჩუქრე სეტი" : "Gift set";
  if (type === "atomizers") return lang === "ka" ? "ატომაიზერები" : "Atomizers";
  if (type === "perfume") return lang === "ka" ? "პარფიუმერია" : "Perfume";
  if (type === "skin-care") return lang === "ka" ? "კანის მოვლა" : "Skin care";
  if (type === "hair-and-body-care") return lang === "ka" ? "თმის და ტანის მოვლა" : "Hair And Body Care";
  if (type === "accessory") return lang === "ka" ? "აქსესუარი" : "Accessory";
  return type;
};

const MyPurchases = () => {
  const { t, language } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [userId, setUserId] = useState(null);
  const [visible, setVisible] = useState(5);
  const [err, setErr] = useState(null);
  const [errMeta, setErrMeta] = useState(null); // errno/sqlstate
  const [loading, setLoading] = useState(true);
  const [openSets, setOpenSets] = useState({});

  useEffect(() => { document.title = t("titles.mypurchases"); }, [language]);

  useEffect(() => {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.width = "";
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    const syncUser = () => {
      try {
        const u = JSON.parse(localStorage.getItem("user") || "null");
        setUserId(u?.id ?? null);
        if (!u?.id) setOrders([]);
      } catch { setUserId(null); setOrders([]); }
    };
    syncUser();
    const onStorage = () => syncUser();
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const fetchPurchases = async () => {
      if (!userId) { setLoading(false); return; }
      setLoading(true); setErr(null); setErrMeta(null);
      try {
        const url = `${API_BASE_URL}/get_purchased_perfumes.php?user_id=${userId}`;
        const res = await api.get(url, { withCredentials: true });
        const raw = res.data;

        // ❗ Normalize: expect array
        if (!Array.isArray(raw)) {
          setOrders([]); setLoading(false); return;
        }

        // set_contents safe parse + ensure arrays later
        const rows = raw.map(r => ({
          ...r,
          set_contents: (() => {
            if (Array.isArray(r?.set_contents)) return r.set_contents;
            if (r?.set_contents == null || r?.set_contents === "") return null;
            try { return JSON.parse(r.set_contents); } catch { return null; }
          })(),
        }));

        // group by order_id
        const byOrder = rows.reduce((acc, row) => {
          const oid = row.order_id;
          if (!acc[oid]) acc[oid] = {
            order_id: oid,
            public_order_id: row.public_order_id || null,  // ← დაამატე
            total_price: row.total_price,
            purchase_date: row.purchase_date,
            items: []
          };
          acc[oid].items.push(row);
          return acc;
        }, {});

        const list = Object.values(byOrder)
          .map(o => ({ ...o, items: asArray(o.items) }))
          .sort((a,b)=> new Date(b.purchase_date) - new Date(a.purchase_date));

        setOrders(list);
      } catch (e) {
        const payload = e?.response?.data;
        setErr(payload?.message || payload?.error || e?.message || "Network error");
        if (payload?.diagnostics) console.log("[SQL diagnostics]", payload.diagnostics);
        setErrMeta({ errno: payload?.errno, sqlstate: payload?.sqlstate });
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, [userId]);

  if (!userId) {
    return (
      <Page>
        <Empty>{t("purchases.noaccess")}</Empty>
      </Page>
    );
  }

  if (loading) {
    return (
      <LoaderWrap><Spinner/></LoaderWrap>
    );
  }

  return (
    <Page>
      <Header>
        <Title>{t("purchases.title")}</Title>
        {err && (
          <Empty style={{color:"crimson",borderColor:"#f3c"}}>
            <div style={{fontWeight:700, marginBottom:6}}>{err}</div>
            {errMeta?.errno || errMeta?.sqlstate ? (
              <div style={{fontSize:12, opacity:.85}}>
                {errMeta?.errno ? <>errno: {errMeta.errno} · </> : null}
                {errMeta?.sqlstate ? <>sqlstate: {errMeta.sqlstate}</> : null}
              </div>
            ) : null}
          </Empty>
        )}
      </Header>

      {orders.length ? orders.slice(0, visible).map((o) => {
        const items = asArray(o.items);
        const itemCount = items.reduce((n,i) => n + Number(i?.quantity||0), 0);
        return (
          <OrderCard key={o.public_order_id || o.order_id}>
            <OrderTop>
              <Row>
                <Meta>#{o.public_order_id} <small>{t("purchases.orderid")}</small></Meta>
                <Meta>{new Date(o.purchase_date).toLocaleDateString()} <small>{t("purchases.date")}</small></Meta>
              </Row>
              <Row>
                <Badge>{itemCount} {t("purchases.quantity")}</Badge>
                <Badge variant="dark">{t("purchases.totalprice")}: {formatMoney(o.total_price)}</Badge>
              </Row>
            </OrderTop>

            <ItemsGrid>
              {items.map((it, idx) => {
                const isSet = it.product_type === "gift_set";
                const isAccessory = it.product_type === "accessory";
                const soldKey = `${o.order_id}_${idx}`;
                const showSet = !!openSets[soldKey];

                const salePer = numOrNull(
                  isAccessory ? it.variant_sale_price : it.sale_price_per
                ) ?? null;

                const basePer = numOrNull(
                  isAccessory ? it.variant_price : (it.base_price_per ?? it.price)
                ) ?? null;

                const colorKa = it.variant_color_ka ?? it.color_ka ?? it.color ?? "";
                const colorEn = it.variant_color_en ?? it.color_en ?? "";
                const displayColor = (language === "ka" ? (colorKa || colorEn) : (colorEn || colorKa)) || "";
                const sizeLabel = it.variant_size_label ?? it.size_label ?? "";

                const setItems = asArray(it.set_contents);

                return (
                  <Item key={soldKey}>
                    <Thumb src={it.image_url || "/placeholder.jpg"} alt={it.name || "Product"} />
                    <div>
                      <Name>{it.name}</Name>

                      <ChipRow>
                        <Badge>{typeLabel(it.product_type, language)}</Badge>

                        {!isAccessory && isPosNumber(it.size) && (
                          <Badge>{t("purchases.size")}: {trimZeros(it.size)} {t("purchases.milliliters")}</Badge>
                        )}

                        {isAccessory && (displayColor || sizeLabel) && (
                          <Badge>
                            {displayColor}{sizeLabel ? ` · ${sizeLabel}` : ""}
                          </Badge>
                        )}

                        <Badge>{t("purchases.quantity")}: {it.quantity}</Badge>
                      </ChipRow>

                      <Price>
                        {salePer && basePer && salePer < basePer ? (
                          <>
                            <span className="old">{formatMoney(basePer)}</span>
                            <span>{formatMoney(salePer)}</span>
                          </>
                        ) : basePer ? (
                          <span>{formatMoney(basePer)}</span>
                        ) : (
                          <span>—</span>
                        )}
                      </Price>

                      {isSet && (
                        <>
                          <SetToggle onClick={() => setOpenSets(s => ({...s, [soldKey]: !s[soldKey]}))}>
                            {showSet ? "− " : "+ "} {language === "ka" ? "რა შედის სეტში" : "What’s inside the set"}
                          </SetToggle>

                          {showSet && setItems.length > 0 && (
                            <SetList>
                              {setItems.map((c, i) => {
                                const hasMl = isPosNumber(c?.ml_size);
                                const unit = numOrNull(c?.override_price) ?? numOrNull(c?.unit_price);
                                const qty  = numOrNull(c?.qty) ?? 1;

                                // accessory-სთვის ფერი/ზომა მოდის backend-იდან color_ka/color_en/size_label ველებით
                                const cColorKa = c?.color_ka ?? "";
                                const cColorEn = c?.color_en ?? "";
                                const cDisplayColor = (language === "ka" ? (cColorKa || cColorEn) : (cColorEn || cColorKa)) || "";
                                const cSizeLabel = c?.size_label ?? "";

                                return (
                                  <SetItem key={i}>
                                    <img src={c?.image_url || "/placeholder.jpg"} alt={c?.name || "Item"} />
                                    <div className="txt">
                                      <div className="nm">{c?.name || "-"}</div>
                                      <div className="sm">
                                        {typeLabel(c?.type, language)}
                                        {hasMl ? ` • ${trimZeros(c?.ml_size)} ml` : ""}
                                        {c?.qty ? ` • x${trimZeros(c?.qty)}` : ""}
                                        {cDisplayColor || cSizeLabel ? ` • ${[cDisplayColor, cSizeLabel].filter(Boolean).join(" · ")}` : ""}
                                      </div>
                                    </div>
                                  </SetItem>
                                );
                              })}
                            </SetList>
                          )}
                        </>
                      )}
                    </div>
                  </Item>
                );
              })}
            </ItemsGrid>

            <Total>{t("purchases.totalprice")}: {formatMoney(o.total_price)}</Total>
          </OrderCard>
        );
      }) : (
        <Empty>{t("purchases.none")}</Empty>
      )}

      {visible < orders.length && (
        <div style={{display:"flex", justifyContent:"center", marginTop:16}}>
          <button
            onClick={() => setVisible(v => v + 5)}
            style={{
              padding:"10px 18px", borderRadius:12, border:"1px solid #ddd",
              background:"#111827", color:"#fff", cursor:"pointer"
            }}
          >
            {t("purchases.loadmore")}
          </button>
        </div>
      )}
    </Page>
  );
};

export default MyPurchases;
